import type { CSSProperties, FC, ReactNode } from 'react'
import type { GetStaticProps } from 'next'
import type { Milestone, Partner } from '@/types'

import { ArrowUpRight } from 'lucide-react'
import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { FigLabel } from '@/components/generals/fig-label'
import { PageHeader } from '@/components/generals/page-header'
import { Reveal } from '@/components/generals/reveal'
import { MilestoneTimeline } from '@/components/studio/milestone-timeline'
import { OfficeClock } from '@/components/studio/office-clock'
import { getMilestones, getPartners } from '@/lib/content'
import { shouldRethrow } from '@/lib/db'
import { pad2 } from '@/lib/format'

import { OFFICES } from '@/constants/navigation'
import { OPEN_ROLES, STUDIO_EMAIL, STUDIO_FACTS } from '@/constants/studio'

interface StudioPageProps {
    partners: Array<Partner>
    milestones: Array<Milestone>
}

const OFFICE_NOTES: Record<string, string> = {
    STO: 'The founding office on Södermalm: architecture, survey and reality capture, computational design and the archive team.',
    LIS: 'By the river in Alcântara: structural engineering, building physics, architecture and the monitoring desk.',
    NYC: 'In a cast-iron loft in NoHo since 2024: architecture, survey and the monitoring desk for North America.'
}

const OFFICE_BUILDINGS: Record<string, string> = {
    STO: 'Former print works, 1911',
    LIS: 'Riverside warehouse, 1928',
    NYC: 'Cast-iron loft, 1886'
}

const delay = (value: number): CSSProperties => ({ '--delay': `${value}ms` } as CSSProperties)

const StudioPage: FC<StudioPageProps> = ({ partners, milestones }): ReactNode => {
    const disciplines = new Set(partners.map(partner => partner.discipline)).size

    return (
        <Layout>
            <Seo
                title='Studio'
                description='Norrvik is a studio of architects, engineers, surveyors and data specialists in Stockholm, Lisbon and New York, founded in 2009.'
                figure='Fig. 01 / Studio / Three offices'
                shape='office-sto'
            />

            <PageHeader
                fig='01'
                title='Studio'
                detail='Stockholm, Lisbon, New York'
                note={`Founded ${STUDIO_FACTS.founded} / ${STUDIO_FACTS.people} people`}
                lines={['Forty people,', 'three offices,', 'one model.']}
                intro={(
                    <p>
                        Founded in Stockholm in 2009, with a Lisbon office since 2013 and New York since 2024. Architects, engineers, surveyors and data specialists work as one team, from the first scan to the last decade of monitoring.
                    </p>
                )}
                readouts={[
                    { label: 'People', value: STUDIO_FACTS.people },
                    { label: 'Partners', value: pad2(partners.length) },
                    { label: 'Disciplines', value: pad2(disciplines) },
                    { label: 'Founded', value: STUDIO_FACTS.founded }
                ]}
            />

            <section aria-labelledby='roster-title' className='border-b border-hairline px-gutter py-20 desk:py-28'>
                <Reveal className='mb-12 flex flex-wrap items-end justify-between gap-6'>
                    <div>
                        <FigLabel fig='02' title='Partners' detail='Roster' note='Name / discipline / city' />

                        <h2 id='roster-title' className='display-lg mt-10'>
                            <span className='reveal-line'>
                                <span className='reveal-inner text-ink'>
                                    The people
                                </span>
                            </span>

                            <span className='reveal-line'>
                                <span className='reveal-inner text-dim' style={{ transitionDelay: '90ms' }}>
                                    on every project.
                                </span>
                            </span>
                        </h2>
                    </div>

                </Reveal>

                {partners.length > 0 ? (
                    <ul className='grid border-l border-t border-hairline sm:grid-cols-2 desk:grid-cols-4'>
                        {partners.map((partner, index) => (
                            <li key={partner.id} className='group flex min-h-[220px] flex-col justify-between gap-10 border-b border-r border-hairline p-5 transition-colors duration-300 hover:bg-panel desk:min-h-[260px] desk:p-6'>
                                <div className='flex items-baseline justify-between gap-4'>
                                    <span className='hud text-mute'>
                                        P.{pad2(index + 1)}
                                    </span>

                                    <span className='hud text-mute'>
                                        <span data-mdlfy-cms={`partners:${partner.id}:city`}>
                                            {partner.city}
                                        </span>
                                    </span>
                                </div>

                                <div>
                                    <p className='font-heading text-[28px] font-medium leading-[1.02] tracking-[-0.025em] text-ink desk:text-[32px]'>
                                        <span data-mdlfy-cms={`partners:${partner.id}:name`}>
                                            {partner.name}
                                        </span>
                                    </p>

                                    <p className='hud mt-4 flex items-center gap-2.5 text-ink'>
                                        <span aria-hidden='true' className='inline-block size-1.5 bg-point transition-colors duration-300 group-hover:bg-signal' />

                                        <span data-mdlfy-cms={`partners:${partner.id}:discipline`}>
                                            {partner.discipline}
                                        </span>
                                    </p>
                                </div>
                            </li>
                        ))}
                    </ul>
                ) : (
                    <p className='hud border border-hairline px-6 py-10 text-center text-mute'>
                        The roster is being updated.
                    </p>
                )}
            </section>

            <section aria-labelledby='timeline-title' className='border-b border-hairline px-gutter py-20 desk:py-28'>
                <Reveal className='mb-12'>
                    <FigLabel fig='03' title='Milestones' detail='2009 to 2026' note='Drag the scrubber or use the arrow keys' />

                    <h2 id='timeline-title' className='sr-only'>
                        Studio milestones from 2009 to 2026
                    </h2>
                </Reveal>

                <MilestoneTimeline milestones={milestones} />
            </section>

            <section aria-labelledby='offices-title' className='border-b border-hairline px-gutter py-20 desk:py-28'>
                <Reveal>
                    <FigLabel fig='04' title='Offices' detail='Local time' note='Stockholm / Lisbon / New York' />

                    <h2 id='offices-title' className='display-lg mt-10'>
                        <span className='reveal-line'>
                            <span className='reveal-inner text-ink' style={delay(80)}>
                                Three offices,
                            </span>
                        </span>

                        <span className='reveal-line'>
                            <span className='reveal-inner text-dim' style={delay(170)}>
                                one working day.
                            </span>
                        </span>
                    </h2>
                </Reveal>

                <div className='mt-12 grid gap-6 desk:grid-cols-3'>
                    {OFFICES.map(office => (
                        <OfficeClock key={office.code} office={office} description={OFFICE_NOTES[office.code] ?? ''} building={OFFICE_BUILDINGS[office.code] ?? 'Studio building'} />
                    ))}
                </div>
            </section>

            <section aria-labelledby='roles-title' className='py-20 desk:py-28'>
                <Reveal className='flex flex-wrap items-end justify-between gap-8 px-gutter pb-12'>
                    <div>
                        <FigLabel fig='05' title='Working with us' detail='Open roles' note={`${pad2(OPEN_ROLES.length)} positions / all offices`} />

                        <h2 id='roles-title' className='display-lg mt-10'>
                            <span className='reveal-line'>
                                <span className='reveal-inner text-ink' style={delay(80)}>
                                    Measure with us,
                                </span>
                            </span>

                            <span className='reveal-line'>
                                <span className='reveal-inner text-dim' style={delay(170)}>
                                    then stay.
                                </span>
                            </span>
                        </h2>
                    </div>

                    <p className='reveal-fade body-copy text-ink/85' style={delay(260)}>
                        Send a short letter and three pieces of work you measured, built or modelled. We reply to every application.
                    </p>
                </Reveal>

                <ul className='border-t border-hairline'>
                    {OPEN_ROLES.map((role, index) => (
                        <li key={role.title} className='group relative border-b border-hairline'>
                            <span className='absolute inset-y-0 left-0 w-[3px] origin-top scale-y-0 bg-signal transition-transform duration-500 ease-instrument group-focus-within:scale-y-100 group-hover:scale-y-100' aria-hidden='true' />

                            <a
                                href={`mailto:${STUDIO_EMAIL}?subject=${encodeURIComponent(`Application: ${role.title}`)}`}
                                className='grid gap-x-8 gap-y-3 px-gutter py-8 transition-colors duration-300 hover:bg-panel desk:grid-cols-[4rem_minmax(0,1.3fr)_minmax(0,1.4fr)_12rem_auto] desk:items-baseline desk:py-10'
                            >
                                <span className='hud text-mute'>
                                    R.{pad2(index + 1)}
                                </span>

                                <span className='font-heading text-[clamp(24px,2.4vw,34px)] font-medium leading-[1.08] tracking-[-0.02em] text-ink'>
                                    {role.title}
                                </span>

                                <span className='text-[15px] leading-[1.6] text-ink/75'>
                                    {role.summary}
                                </span>

                                <span className='hud text-ink'>
                                    {role.office}
                                    <span className='block text-mute'>
                                        {role.terms}
                                    </span>
                                </span>

                                <span className='hud flex items-center gap-2 text-ink'>
                                    Apply
                                    <ArrowUpRight className='size-4 transition-transform duration-500 ease-instrument group-hover:-translate-y-0.5 group-hover:translate-x-0.5' strokeWidth={1.5} aria-hidden='true' />
                                </span>
                            </a>
                        </li>
                    ))}
                </ul>
            </section>
        </Layout>
    )
}

export const getStaticProps: GetStaticProps<StudioPageProps> = async () => {
    try {
        const [partners, milestones] = await Promise.all([getPartners(), getMilestones()])

        return { props: { partners, milestones }, revalidate: 60 }
    }
    catch (error) {
        console.error('Studio content could not be loaded:', error instanceof Error ? error.message : error)

        if (shouldRethrow()) throw error

        return { props: { partners: [], milestones: [] }, revalidate: 30 }
    }
}

export default StudioPage
